import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Edit, Eye, Loader2, UserCheck, UserX } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getMentors,
  Mentor,
  MentorsKeys,
  updateMentor,
} from "@/features/mentors/api/mentors";

import MentorDetailsDialog from "./MentorDetailsDialog";

interface MentorsTableProps {
  setMentorId: (id: string) => void;
}

const MentorsTable: React.FC<MentorsTableProps> = ({ setMentorId }) => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: [MentorsKeys.GET_MENTORS],
    queryFn: getMentors,
  });

  const mentors = Array.isArray(data) ? data : [];

  const [viewMentorId, setViewMentorId] = React.useState<string | null>(null);
  const [toggleMentor, setToggleMentor] = React.useState<Mentor | null>(null);

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateMentor(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MentorsKeys.GET_MENTORS] });
      setToggleMentor(null);
    },
  });

  if (isLoading) {
    return (
      <Card className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </Card>
    );
  }

  const isDeactivating = toggleMentor?.isActive !== false;

  return (
    <>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mentors.map((mentor) => {
              const active = mentor.isActive !== false;
              return (
                <TableRow
                  key={mentor._id}
                  className={active ? "" : "opacity-50"}
                >
                  <TableCell
                    className="cursor-pointer"
                    onClick={() => setViewMentorId(mentor._id)}
                  >
                    {`${mentor.firstName} ${mentor.lastName}`}
                  </TableCell>
                  <TableCell
                    className="cursor-pointer"
                    onClick={() => setViewMentorId(mentor._id)}
                  >
                    {mentor.email}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="opaque"
                      color={active ? "green" : "red"}
                    >
                      {active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className="cursor-pointer"
                    onClick={() => setViewMentorId(mentor._id)}
                  >
                    {format(new Date(mentor.createdAt), "dd/MM/yyyy")}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setViewMentorId(mentor._id)}
                        className="gap-1"
                      >
                        <Eye className="h-3 w-3" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setMentorId(mentor._id)}
                        className="gap-1"
                      >
                        <Edit className="h-3 w-3" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setToggleMentor(mentor)}
                        className="gap-1"
                      >
                        {active ? (
                          <>
                            <UserX className="h-3 w-3" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <UserCheck className="h-3 w-3" />
                            Reactivate
                          </>
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <MentorDetailsDialog
          mentorId={viewMentorId}
          onClose={() => setViewMentorId(null)}
        />
      </Card>

      <AlertDialog
        open={!!toggleMentor}
        onOpenChange={(open) => !open && setToggleMentor(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isDeactivating ? "Deactivate" : "Reactivate"}{" "}
              {toggleMentor?.firstName} {toggleMentor?.lastName}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isDeactivating
                ? "This mentor will be hidden from the payouts table. You can reactivate them at any time."
                : "This mentor will appear in the payouts table again."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggleActiveMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={toggleActiveMutation.isPending}
              onClick={() => {
                if (!toggleMentor) return;
                toggleActiveMutation.mutate({
                  id: toggleMentor._id,
                  isActive: !isDeactivating,
                });
              }}
            >
              {toggleActiveMutation.isPending
                ? "Saving..."
                : isDeactivating
                  ? "Deactivate"
                  : "Reactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MentorsTable;
